/**
 * Download PDF Button Component
 * Modern Playwright-based PDF download using server-side rendering
 */

import React, { useState } from 'react';
import { Download } from 'lucide-react';

const DownloadPDFButton = ({
  type,           // 'valuation', 'gap-analysis', 'client-profile', or 'advisory-guide'
  subType,        // For valuation: 'advanced'/'basic', for gap: 'profit'/'value'/'wealth' (not needed for client-profile or advisory-guide)
  businessId,
  label = 'Download PDF',
  className = '',
  variant = 'primary',  // 'primary' or 'secondary'
  forecastParams  // { growthRate, ebitdaMargin, years, confidenceRange } - only for valuation PDFs
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      // Build API endpoint
      let endpoint;
      if (type === 'valuation') {
        endpoint = `/pdf/valuation/${subType}/${businessId}`;
      } else if (type === 'gap-analysis') {
        endpoint = `/pdf/gap-analysis/${subType}/${businessId}`;
      } else if (type === 'client-profile') {
        endpoint = `/pdf/client-profile/${businessId}`;
      } else if (type === 'advisory-guide') {
        endpoint = `/pdf/advisory-guide`;
      } else if (type === 'goal-setting') {
        endpoint = `/pdf/goal-setting/${businessId}`;
      } else {
        throw new Error(`Unknown PDF type: ${type}`);
      }

      let apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${endpoint}`;

      // Add forecast parameters as query string for valuation PDFs
      if (type === 'valuation' && forecastParams) {
        const params = new URLSearchParams();
        if (forecastParams.growthRate !== undefined) params.append('growthRate', forecastParams.growthRate);
        if (forecastParams.ebitdaMargin !== undefined) params.append('ebitdaMargin', forecastParams.ebitdaMargin);
        if (forecastParams.years !== undefined) params.append('years', forecastParams.years);
        if (forecastParams.confidenceRange !== undefined) params.append('confidenceRange', forecastParams.confidenceRange);

        const queryString = params.toString();
        if (queryString) {
          apiUrl += `?${queryString}`;
        }
      }

      // Fetch PDF
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate PDF' }));
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      // Get filename from headers or generate default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'report.pdf';
      if (contentDisposition) {
        // Try multiple patterns to extract filename
        const filenameMatch = contentDisposition.match(/filename\*?=["']?([^"';]+)["']?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
          // Remove UTF-8 encoding prefix if present
          filename = filename.replace(/^UTF-8''/, '');
          // Decode URI encoding if present
          filename = decodeURIComponent(filename);
        }
      }


      // Download PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(err.message);
      alert(`Failed to download PDF: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const baseStyles = 'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  const variantStyles = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
  };

  return (
    <button
      onClick={handleDownload}
      disabled={loading}
      className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      title={loading ? 'Generating PDF...' : 'Download PDF report'}
    >
      {loading ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span>Generating...</span>
        </>
      ) : (
        <>
          <Download size={18} />
          <span>{label}</span>
        </>
      )}
    </button>
  );
};

export default DownloadPDFButton;
